/* eslint-disable jsx-a11y/no-static-element-interactions */
import React from "react";
import { Link } from "react-router-dom";
import { css, keyframes } from "@emotion/react";
import styled from "@emotion/styled";

interface SessionButtonProps {
  children: React.ReactNode;
  type?: "button" | "submit" | "reset" | "internalLink";
  link?: string;
  // eslint-disable-next-line no-unused-vars
  onClick?: (event: React.MouseEvent, ...args: unknown[]) => void;
  // eslint-disable-next-line no-unused-vars
  onMouseDown?: (event: React.MouseEvent) => void;
  disabled?: boolean;
  loading?: boolean;
  styleType?: "largePrimary" | "largeSecondary";
  darkModeAllowed?: boolean;
  [key: string]: any;
}

const SessionButton: React.FC<SessionButtonProps> = ({
  children,
  type = "submit",
  link,
  onClick,
  onMouseDown,
  disabled = false,
  loading = false,
  styleType = "largePrimary",
  darkModeAllowed = true,
  ...props
}) => {
  const handleClick = (event: React.MouseEvent, ...args: unknown[]) => {
    if (!disabled && onClick) {
      onClick(event, ...args);
    } else if (disabled) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const handleOnMouseDown = (event: React.MouseEvent) => {
    if (onMouseDown) onMouseDown(event);
  };

  const getStyles = (t: any, isLoading: boolean) => {
    const typeStyles = getTypeStyles(t, darkModeAllowed);
    return css`
      ${[typeStyles, t.text.medium, t.rounded.sm, t.h(9)]}
      white-space: nowrap;
      border: none;
      appearance: none;
      position: relative;
      cursor: pointer;
      max-width: 100%;
      &:disabled {
        opacity: ${isLoading ? 1 : 0.5};
        cursor: default;
      }
    `;
  };

  const getTypeStyles = (t: any, darkModeAllowed: boolean) => {
    const buttonShape = css`
      ${[t.h(9), t.text.sm, t.px(6)]}
      width: auto;
      display: ${type === "internalLink" ? "inline-flex" : "flex"};
      align-items: center;
      justify-content: center;
      outline: none;
    `;

    switch (styleType) {
      case "largePrimary":
        return css`
          ${buttonShape};
          color: ${darkModeAllowed && t.dark ? t.color.black : t.color.white};
          &:link,
          &:visited {
            color: ${darkModeAllowed && t.dark ? t.color.black : t.color.white};
          }
          background: ${darkModeAllowed && t.dark
            ? "linear-gradient(120deg, #FBD7FF 10%, #FFDEC1 90%)"
            : t.color.black};
          transition: background 0.2s ease, box-shadow 0.2s ease;
          &:hover:not([disabled]) {
            background: ${darkModeAllowed && t.dark
              ? "linear-gradient(to right, #FBD7FF -30%, #FFDEC1 70%)"
              : t.color.gray[700]};
          }
          &:focus {
            ${darkModeAllowed && t.dark
              ? `box-shadow: 0 0 0 2px ${t.color.gray[500]}`
              : `box-shadow: 0 0 0 2px ${t.color.gray[400]}`};
          }
          > span {
            overflow-x: hidden;
            line-height: 1.6;
            white-space: nowrap;
            text-overflow: ellipsis;
          }
        `;
      case "largeSecondary":
        return css`
          ${buttonShape}
          background-color: ${darkModeAllowed && t.dark ? t.color.gray[700] : t.color.gray[200]};
          transition: background-color 0.2s ease, box-shadow 0.2s ease;
          color: ${darkModeAllowed && t.dark ? t.color.gray[200] : t.color.black};
          &:link,
          &:visited {
            color: ${darkModeAllowed && t.dark ? t.color.gray[200] : t.color.black};
          }
          border: 1px solid ${t.color.gray[200]};
          &:hover:not([disabled]) {
            color: ${darkModeAllowed && t.dark ? t.color.white : t.color.black};
            background-color: ${darkModeAllowed && t.dark ? t.color.gray[600] : t.color.gray[300]};
          }
          &:focus {
            box-shadow: 0 0 0 2px ${darkModeAllowed && t.dark ? t.color.gray[500] : t.color.gray[300]};
          }
          &:hover:focus {
            box-shadow: 0 0 0 2px ${darkModeAllowed && t.dark ? t.color.gray[500] : t.color.gray[300]};
          }
          > span {
            overflow-x: hidden;
            line-height: 1.6;
            white-space: nowrap;
            text-overflow: ellipsis;
          }
        `;
      default:
        return buttonShape;
    }
  };

  /* TYPE TRANSITION
  ======================================================= */
  switch (type) {
    case "internalLink":
      return (
        <Link
          to={link || "/"}
          {...props}
          onClick={handleClick}
          css={(theme) => getStyles(theme, loading)}
        >
          <span>{children}</span>
        </Link>
      );
    default:
      return (
        <button
          type={type}
          css={(theme) => getStyles(theme, loading)}
          {...props}
          onClick={handleClick}
          onMouseDown={handleOnMouseDown}
          disabled={disabled}
        >
          <span>{children}</span>
          {loading && (
            <Styled.Loader styleType={styleType} darkModeAllowed={darkModeAllowed} data-testid="loading-animation">
              <div />
              <div />
              <div />
            </Styled.Loader>
          )}
        </button>
      );
  }
};

export default SessionButton;

/* Styled */
const Styled: any = {};

Styled.Loader = styled.div<{ styleType: string; darkModeAllowed?: boolean }>((props: any) => {
  const t = props.theme;
  const darkModeAllowed = props.darkModeAllowed;
  return css`
    label: SessionButton_Loader;
    position: absolute;
    display: flex;
    justify-content: center;
    align-items: center;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    height: 100%;
    opacity: 100%;
    &:before {
      ${t.rounded.sm}
      background: ${props.styleType === "largePrimary"
        ? (darkModeAllowed && t.dark)
          ? "linear-gradient(to right, #FBD7FF -40%, #FFDEC1 80%)"
          : t.color.gray[700]
        : darkModeAllowed && t.dark
        ? t.color.gray[600]
        : t.color.gray[300]};
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      display: block;
      content: "";
    }
    div {
      position: relative;
      width: 0.125rem;
      margin: 0.125rem;
      background-color: ${props.styleType === "largePrimary"
        ? darkModeAllowed && t.dark
          ? t.color.black
          : t.color.white
        : darkModeAllowed && t.dark
        ? t.color.gray[200]
        : t.color.black};
      animation: ${loadingAnimation} 1.6s cubic-bezier(0, 0.5, 0.5, 1) infinite;
    }
    div:nth-of-type(1) {
      animation-delay: -0.24s;
    }
    div:nth-of-type(2) {
      animation-delay: -0.12s;
    }
    div:nth-of-type(3) {
      animation-delay: 0;
    }
  `;
});

const loadingAnimation = keyframes`
  0% { height: 0.75rem; }
  30%, 100% { height: 0.125rem; }
`;
